import { useEffect, useRef, useState } from 'react';
import type { ProviderKind } from '@hangar/shared';
import { verifyProviderToken } from '@/api/endpoints';
import type { ProviderTokenVerifyResult } from '@/api/schemas';
import { canVerifyToken, providerVerifyKey } from './providerTokenModel';

/**
 * Verify an Expo or Bitrise token before anything is stored: asks the API what the pasted token reaches (the accounts or
 * workspaces it sees) and whether it can build on the chosen one, on its own once per combination of provider, token
 * and account, or on the button. The answer is shown only while what it was asked for is unchanged.
 */
export function useVerifyProviderToken(values: { kind: ProviderKind; token: string; externalAccount: string }) {
  const [answer, setAnswer] = useState<{ key: string; result: ProviderTokenVerifyResult } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const key = providerVerifyKey(values);
  const ready = canVerifyToken(values.token);

  const verify = async (): Promise<ProviderTokenVerifyResult> => {
    setBusy(true);
    setError(null);
    try {
      const account = values.externalAccount.trim();
      const result = await verifyProviderToken({ kind: values.kind, token: values.token.trim(), ...(account ? { externalAccount: account } : {}) });
      setAnswer({ key, result });
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setBusy(false);
    }
  };
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || asked.current === key) return;
    const timer = setTimeout(() => {
      asked.current = key;
      void verify().catch(() => undefined);
    }, 400);
    return () => clearTimeout(timer);
    // `verify` reads the values of this render; the key says when they changed.
  }, [ready, key]);
  return { result: answer?.key === key ? answer.result : null, busy, error, ready, verify };
}

export type VerifyProviderToken = ReturnType<typeof useVerifyProviderToken>;
